// src/prototype/enterprise/governance/GovernanceModal.tsx
// ============================================================
// ENTERPRISE · GOVERNANCE FRAMING (design "1j") — the "route to governance" modal.
//
// Connects Scenarios → Governance → Decisions → Reports. Per queued initiative: the real
// ApprovalChain (Submitted → Committee → CFO capital gate → Logged), the CFO capital-gate check
// in the CFOPanel shape (ask vs remaining under cap → fits), the recorded rationale, and
// Approve/Defer/Hold. Ratifying advances the queue (demo-only; the spine is read-only). When the
// queue is done, the export moment links to the Reports library / decision record.
// ============================================================

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Check } from 'lucide-react';
import { ApprovalChain } from '../../components/ApprovalChain';
import { buildGovernanceQueue } from './governanceModel';

const DECISION_LABEL: Record<string, string> = { APPROVE: 'Approved', DEFER: 'Deferred', HOLD: 'Held', KILL: 'Killed' };

export const GovernanceModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const navigate = useNavigate();
  const queue = useMemo(() => buildGovernanceQueue(), []);
  const [idx, setIdx] = useState(0);
  const [ratified, setRatified] = useState<string[]>([]);

  const total = queue.items.length;
  const complete = idx >= total;
  const item = complete ? null : queue.items[idx];

  const goTo = (tab: 'reports' | 'decisions') => { onClose(); navigate(`/enterprise/${tab}`); };
  const ratify = (verb: string) => {
    setRatified((r) => [...r, verb]);
    setIdx((i) => i + 1);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0f1a2c]/55 px-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-[820px] max-w-full max-h-[88vh] overflow-auto overflow-x-hidden bg-white rounded-[14px]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 bg-[#18365E] flex justify-between items-start gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#8fa5c4]">
              Route to governance · from scenario {queue.scenarioName}
              {!complete && total > 0 && ` · decision ${idx + 1} of ${total}`}
            </div>
            <div className="text-[15px] font-bold text-white mt-0.5">{complete ? 'All decisions routed' : item?.title}</div>
            <div className="text-[10.5px] text-[#b7c4d8] mt-0.5">
              {complete
                ? `${ratified.length || total} decisions written to the record under ${queue.scenarioName}`
                : `Composite ${item?.composite.toFixed(1)} · Rank #${item?.rank}${item?.funded ? ' · funded' : ' · below line'} · Capital ask $${item?.ask.toFixed(1)}M`}
            </div>
          </div>
          <button onClick={onClose} className="text-[#8fa5c4] text-lg leading-none hover:text-white" aria-label="Close">✕</button>
        </div>

        {!queue.found ? (
          <div className="px-6 py-16 text-center text-[12px] text-[#9ca3af]">No saved scenario to route.</div>
        ) : complete ? (
          /* Export moment */
          <div className="px-6 py-8">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-[#15803d] mb-1">
              <Check className="w-4 h-4" /> All routed decisions logged to the record
            </div>
            <div className="text-[11px] text-[#717182] mb-5">
              Every decision is timestamped and attributed under {queue.scenarioName}. The governance-ready outputs are generated from the saved scenario + the decision record.
            </div>
            <div className="flex flex-wrap gap-2.5">
              <button onClick={() => goTo('reports')} className="bg-[#FF6E4C] text-white rounded-lg px-4 py-2.5 text-[12px] font-bold hover:bg-[#f2593a]">
                Generate for committee → Reports
              </button>
              <button onClick={() => goTo('decisions')} className="border-[1.5px] border-[#18365E] text-[#18365E] rounded-lg px-4 py-2.5 text-[12px] font-semibold bg-white hover:bg-[#18365E]/[0.04]">
                View decision record
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Approval chain (real ApprovalChain) + CFO capital gate */}
            <div className="flex flex-col md:flex-row gap-5 px-6 py-5 border-b border-[#e5e7eb]">
              <div className="md:w-[240px] flex-none">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#717182] mb-2.5">Approval chain</div>
                <ApprovalChain steps={item!.stages} />
              </div>

              <div className="flex-1 min-w-0 flex flex-col gap-3">
                {/* CFO gate · capital check — CFOPanel shape */}
                <div className="rounded-xl border border-[#18365E]/20 bg-[#18365E]/[.03] p-3.5">
                  <div className="flex items-center gap-2 mb-2.5">
                    <DollarSign className="w-4 h-4 text-[#18365E]" />
                    <span className="text-[11px] font-semibold text-[#18365E] uppercase tracking-wide">CFO gate · capital check</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2.5">
                    <div className="bg-white rounded-lg border border-[#e5e7eb] px-3 py-2">
                      <div className="text-[9px] text-[#717182] font-semibold uppercase tracking-wide">Capital ask</div>
                      <div className="text-[15px] font-bold text-[#18365E] mt-0.5">${item!.ask.toFixed(1)}M</div>
                    </div>
                    <div className="bg-white rounded-lg border border-[#e5e7eb] px-3 py-2">
                      <div className="text-[9px] text-[#717182] font-semibold uppercase tracking-wide">Remaining · ${item!.cap.toFixed(1)}M cap</div>
                      <div className="text-[15px] font-bold text-[#18365E] mt-0.5">${item!.remaining.toFixed(1)}M</div>
                    </div>
                    <div className="bg-white rounded-lg border border-[#e5e7eb] px-3 py-2">
                      <div className="text-[9px] text-[#717182] font-semibold uppercase tracking-wide">Fits within cap</div>
                      <div className="text-[15px] font-bold mt-0.5" style={{ color: item!.fits ? '#15803d' : '#b91c1c' }}>
                        {item!.fits ? 'Yes ✓' : 'No — exceeds'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rationale */}
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#717182] mb-1.5">Rationale · required, written to the record</div>
                  <div className="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[11px] leading-relaxed text-[#374151] min-h-[58px]">
                    {item!.rationale}
                  </div>
                  <div className="text-[9.5px] text-[#9ca3af] mt-1">On the record: {item!.recordedBy} · currently <b className="text-[#374151]">{DECISION_LABEL[item!.recordedDecision] ?? item!.recordedDecision}</b></div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap justify-between items-center gap-3 px-6 py-3.5">
              <div className="flex gap-2">
                <button onClick={() => ratify('APPROVE')} className="bg-[#15803d] text-white rounded-lg px-4 py-2 text-[12px] font-bold hover:bg-[#136c34]">Approve</button>
                <button onClick={() => ratify('DEFER')} className="border border-[#e5e7eb] text-[#475569] rounded-lg px-4 py-2 text-[12px] font-semibold bg-white hover:bg-[#f8fafc]">Defer</button>
                <button onClick={() => ratify('HOLD')} className="border border-[#e5e7eb] text-[#475569] rounded-lg px-4 py-2 text-[12px] font-semibold bg-white hover:bg-[#f8fafc]">Hold</button>
              </div>
              <div className="text-[10px] text-[#9ca3af]">Decision writes a timestamped, attributed entry to the decision record</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GovernanceModal;
